import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import CheckBox from "@/js/common/ui/input/CheckBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import Util from "@/js/common/Util";
import React from "react";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import { downloadPdfFromElement } from "@/js/system/common/print/PdfUtil";
import MbrasInfoPdf from "../pdf/MbrasInfoPdf";

interface Props {
    mbrasId: string;
    setModalOpen: Function;
    setCallTime: Function;
}

/* 최초 조회 시 비어있던 항목만 수정 허용 */
interface EditableEmptyFields {
    telno           : boolean;
    brdtDate        : boolean;
    gndrCd          : boolean;
    upOgnzCd        : boolean;
    lbrShpSeCd      : boolean;
    wtjrNm          : boolean;
    ogdpOrgCd       : boolean;
    mbrasJoinSttsCd : boolean;
    ocptTypeCd      : boolean;
    frstJncmpYmd    : boolean;
    joinYmd         : boolean;
    aprvYmd         : boolean;
}

const MbrasMainMng = ({ mbrasId, setModalOpen, setCallTime }: Props) => {
    const userInfo = useRecoilValue(Atom.userInfo);

    /* 조합원 관리자 여부 */
    const isMemberAdmin = userInfo.memberAdmin === "Y";

    /* 기본 정보 */
    const [item            , setItem            ] = React.useState<any>({});
    const [mbrasNm         , setMbrasNm         ] = React.useState<string>("");
    const [telno           , setTelno           ] = React.useState<string>("");
    const [brdtDate        , setBrdtDate        ] = React.useState<string>("");
    const [gndrCd          , setGndrCd          ] = React.useState<string>("");
    const [upOgnzCd        , setUpOgnzCd        ] = React.useState<string>("");
    const [lbrShpSeCd      , setLbrShpSeCd      ] = React.useState<string>("");
    const [wtjrNm          , setWtjrNm          ] = React.useState<string>("");
    const [ogdpOrgCd       , setOgdpOrgCd       ] = React.useState<string>("");
    const [mbrasJoinSttsCd , setMbrasJoinSttsCd ] = React.useState<string>("");
    const [ocptTypeCd      , setOcptTypeCd      ] = React.useState<string>("");
    const [frstJncmpYmd    , setFrstJncmpYmd    ] = React.useState<string>("");
    const [leaveTypeCd     , setLeaveTypeCd     ] = React.useState<string>("");
    const [leaveStartDt    , setLeaveStartDt    ] = React.useState<string>("");
    const [leaveEndDt      , setLeaveEndDt      ] = React.useState<string>("");
    const [remainDay       , setRemainDay       ] = React.useState<string>("");
    const [wthdAgreCd      , setWthdAgreCd      ] = React.useState<string>("");

    /* 기존 입력값 수정 체크 */
    const [telnoEdit       , setTelnoEdit       ] = React.useState<boolean>(false);
    const [ocptTypeEdit    , setOcptTypeEdit    ] = React.useState<boolean>(false);
    const [wtjrNmEdit      , setWtjrNmEdit      ] = React.useState<boolean>(false);

    /* 가입 이력 */
    const [historyList     , setHistoryList     ] = React.useState<any[]>([]);
    const [joinYmd         , setJoinYmd         ] = React.useState<string>("");
    const [aprvYmd         , setAprvYmd         ] = React.useState<string>("");
    const [rglrRegMm       , setRglrRegMm       ] = React.useState<string>("");
    const [joinPeriod      , setJoinPeriod      ] = React.useState<string>("");

    /* 기타 정보 */
    const [addr            , setAddr            ] = React.useState<string>("");
    const [memo            , setMemo            ] = React.useState<string>("");
    const [whdwlYn         , setWhdwlYn         ] = React.useState<string>("");
    const [whdwlRsnCn      , setWhdwlRsnCn      ] = React.useState<string>("");
    const printRef = React.useRef<HTMLDivElement>(null);

    /* PDF 정보 */
    const [pdfHistoryList   , setPdfHistoryList   ] = React.useState<any[]>([]);
    const [pdfReady         , setPdfReady         ] = React.useState<boolean>(false);

    const handlePdfDownload = () => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url         : "system/mbras/mng/select-mbras-pdf-info.do",
            methodType  : "post",
            paramType   : "json",
            paramData   : { mbrasId: mbrasId },
            onSuccessFn : (res: any) => {
                setPdfHistoryList   (res.result.list || []);
                setPdfReady         (true);
            },
        });
    };

    React.useEffect(() => {
        if (!pdfReady) return;

        const createPdf = async () => {
            await downloadPdfFromElement(
                printRef.current,
                `조합원정보_${mbrasNm}.pdf`
            );

            setPdfReady(false);
            AppContext.hideSpinner();
        };

        createPdf();
    }, [pdfReady, pdfHistoryList, joinPeriod, mbrasNm]);

    /* 최초 조회 시 비어있던 항목 */
    const [editableEmptyFields, setEditableEmptyFields] = React.useState<EditableEmptyFields>({
        telno            : false,
        brdtDate         : false,
        gndrCd           : false,
        upOgnzCd         : false,
        lbrShpSeCd       : false,
        wtjrNm           : false,
        ogdpOrgCd        : false,
        mbrasJoinSttsCd  : false,
        ocptTypeCd       : false,
        frstJncmpYmd     : false,
        joinYmd          : false,
        aprvYmd          : false,
    });

    /* 탈퇴 조합원 여부 */
    const isWithdraw = String(whdwlYn) === "Y" || String(mbrasJoinSttsCd) === "03";

    /* 휴직 조합원 여부 */
    const isLeave = String(mbrasJoinSttsCd) === "02";

    /* 수정 불가 상태 여부 : 탈퇴(03), 의원면직(06), 퇴직(07) */
    const isReadOnlyStatus = ["03", "06", "07"].includes(String(mbrasJoinSttsCd));

    /* 주소,메모 조합원 관리자가 수정 가능 */
    const canEdit = isMemberAdmin && !isReadOnlyStatus;

    /* 최초에 비어있던 항목만 수정 가능 */
    const canEditIfInitiallyEmpty = (fieldName: keyof EditableEmptyFields) => {
        return isMemberAdmin && !isReadOnlyStatus && editableEmptyFields[fieldName];
    };

    /* 기존값이 있는 항목은 체크박스 선택 시 수정 가능 */
    const canEditTelno = isMemberAdmin && !isReadOnlyStatus && (editableEmptyFields.telno || telnoEdit);
    const canEditOcptType = isMemberAdmin && !isReadOnlyStatus && (editableEmptyFields.ocptTypeCd || ocptTypeEdit);
    const canEditWtjrNm = isMemberAdmin && !isReadOnlyStatus && (editableEmptyFields.wtjrNm || wtjrNmEdit);

    /* 가입신청일, 가입승인일 중 미입력 항목 여부 */
    const showJoinDateFields = editableEmptyFields.joinYmd || editableEmptyFields.aprvYmd;

    // 근로형태 값에 따라 사용할 "직종 공통코드 그룹" 결정
    const ocptGroupCd = lbrShpSeCd === "01" ? "PMEMP_OCPT_TYPE_CD" : lbrShpSeCd === "02" ? "IREMP_OCPT_TYPE_CD" : "";

    React.useEffect(() => {
        const safeStr = (v: any) => (Util.isEmpty(v) ? "" : String(v));

        AppContext.showSpinner();

        Axios.dataAccess({
            url         : "system/mbras/mng/select-mbras-dtl.do",
            methodType  : "post",
            paramType   : "json",
            paramData   : { mbrasId: mbrasId },
            onSuccessFn : (res: any) => {
                const item            = res.result.item || {};
                const history         = res.result.history || [];
                const joinPeriodItem  = res.result.joinPeriod || {};
                const nextWhdwlYn     = safeStr(item.whdwlYn);
                const nextJoinSttsCd  = safeStr(item.mbrasJoinSttsCd);

                setItem           (item);
                setHistoryList    (history);
                setWhdwlYn        (nextWhdwlYn);
                setWhdwlRsnCn     (safeStr(item.whdwlRsnCn));

                setMbrasNm        (safeStr(item.mbrasNm));
                setTelno          (safeStr(item.telno));
                setBrdtDate       (safeStr(item.brdtDate));
                setGndrCd         (safeStr(item.gndrCd));
                setUpOgnzCd       (safeStr(item.upOgnzCd));
                setOgdpOrgCd      (safeStr(item.ogdpOrgCd));
                setWtjrNm         (safeStr(item.wtjrNm));
                setLbrShpSeCd     (safeStr(item.lbrShpSeCd));
                setOcptTypeCd     (safeStr(item.ocptTypeCd));
                setMbrasJoinSttsCd(nextJoinSttsCd);
                setFrstJncmpYmd   (safeStr(item.frstJncmpYmd));
                setLeaveTypeCd    (safeStr(item.leaveTypeCd));
                setLeaveStartDt   (safeStr(item.leaveStartDt));
                setLeaveEndDt     (safeStr(item.leaveEndDt));
                setRemainDay      (safeStr(item.remainDay));
                setWthdAgreCd     (safeStr(item.wthdStatusCd));
                setJoinYmd        (safeStr(item.joinYmd));
                setAprvYmd        (safeStr(item.aprvYmd));
                setRglrRegMm      (safeStr(item.rglrRegMm));
                setAddr           (safeStr(item.addr));
                setMemo           (safeStr(item.memo));
                const years = Number(joinPeriodItem.years ?? 0);
                const months = Number(joinPeriodItem.months ?? 0);
                setJoinPeriod(years === 0 ? `${months}개월` : `${years}년 ${months}개월`);

                const withdrawLoaded = String(nextWhdwlYn) === "Y" || String(nextJoinSttsCd) === "03";

                setTelnoEdit(false);
                setOcptTypeEdit(false);
                setWtjrNmEdit(false);

                setEditableEmptyFields({
                    telno            : !withdrawLoaded && Util.isEmpty(item.telno),
                    brdtDate         : !withdrawLoaded && Util.isEmpty(item.brdtDate),
                    gndrCd           : !withdrawLoaded && Util.isEmpty(item.gndrCd),
                    upOgnzCd         : !withdrawLoaded && Util.isEmpty(item.upOgnzCd),
                    lbrShpSeCd       : !withdrawLoaded && Util.isEmpty(item.lbrShpSeCd),
                    wtjrNm           : !withdrawLoaded && Util.isEmpty(item.wtjrNm),
                    ogdpOrgCd        : !withdrawLoaded && Util.isEmpty(item.ogdpOrgCd),
                    mbrasJoinSttsCd  : !withdrawLoaded && Util.isEmpty(item.mbrasJoinSttsCd),
                    ocptTypeCd       : !withdrawLoaded && Util.isEmpty(item.ocptTypeCd),
                    frstJncmpYmd     : !withdrawLoaded && Util.isEmpty(item.frstJncmpYmd),
                    joinYmd          : !withdrawLoaded && Util.isEmpty(item.joinYmd),
                    aprvYmd          : !withdrawLoaded && Util.isEmpty(item.aprvYmd),
                });

                AppContext.hideSpinner();
            },
        });
    }, [mbrasId]);

    const saveMbras = () => {
        if (!isMemberAdmin) {
            AppContext.openAlert({
                title : "권한 확인",
                msg   : "조합원 관리자만 저장할 수 있습니다.",
                icon  : "warning",
            });
            return;
        }

        // 휴대전화번호 최대 자리수 확인
        const phoneNumber = telno.replace(/[^0-9]/g, "");

        if (phoneNumber.length > 11) {
            AppContext.openAlert({
                title : "휴대전화번호 확인",
                msg   : "휴대전화번호는 최대 11자리까지 입력할 수 있습니다.",
                icon  : "warning",
            });
            return;
        }

        // 근로형태가 선택되어 있는데 직종이 비어있는 경우 저장 방지
        if (!Util.isEmpty(lbrShpSeCd) && Util.isEmpty(ocptTypeCd)) {
            AppContext.openAlert({
                title : "직종 확인",
                msg   : "근로형태가 선택된 경우 직종을 반드시 선택해야 합니다.",
                icon  : "warning",
            });
            return;
        }

        // 가입신청일은 가입승인일보다 늦을 수 없음
        if (!Util.isEmpty(joinYmd) && !Util.isEmpty(aprvYmd) && joinYmd > aprvYmd) {
            AppContext.openAlert({
                title : "가입일자 확인",
                msg   : "가입신청일은 가입승인일보다 늦을 수 없습니다.",
                icon  : "warning",
            });

            return;
        }

        AppContext.openAlert({
            title         : "저장",
            msg           : "조합원 정보를 저장하시겠습니까?",
            icon          : "warning",
            confirmText   : "저장",
            handleConfirm : () => {
                AppContext.showSpinner();

                Axios.dataAccess({
                    url         : "system/mbras/mng/save_mbras_mng.do",
                    methodType  : "post",
                    paramType   : "json",
                    paramData   : {
                        mbrasId          : mbrasId,
                        mbrasNm          : mbrasNm.trim(),
                        telno            : telno,
                        brdtDate         : brdtDate,
                        gndrCd           : gndrCd,
                        lbrShpSeCd       : lbrShpSeCd,
                        wtjrNm           : wtjrNm.trim(),
                        ogdpOrgCd        : ogdpOrgCd,
                        mbrasJoinSttsCd  : mbrasJoinSttsCd,
                        ocptTypeCd       : ocptTypeCd,
                        frstJncmpYmd     : frstJncmpYmd,
                        joinYmd          : joinYmd,
                        aprvYmd          : aprvYmd,
                        addr             : addr.trim(),
                        memo             : memo.trim(),
                    },
                    onSuccessFn : () => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title : "완료",
                            msg   : "저장되었습니다.",
                            icon  : "success",
                        });

                        setCallTime(new Date());
                        setModalOpen(false);
                    },
                });
            },
        });
    };

    const closeModal = () => setModalOpen(false);

    const optionalFields: { key: string; label: React.ReactNode; content: React.ReactNode }[] = [];

    if (lbrShpSeCd === "01") {
        optionalFields.push({
            key   : "frstJncmpYmd",
            label : (
                <div>
                    <div className="fm-b fs13 fc111111">
                        입직년월일
                    </div>
                    <div className="fm-c fs11 fc4A90A4 mt2">
                        정규직 임용일자
                    </div>
                </div>
            ),
            content: (
                <DatePickerBox
                    width={230}
                    height={36}
                    fontSize={14}
                    fontFamily="fm-c"
                    color="#333"
                    letterSpacing={-1}
                    borderColor="#ddd"
                    borderOnColor={canEditIfInitiallyEmpty("frstJncmpYmd") ? "#222" : "#ddd"}
                    borderRadius={5}
                    bgColor={canEditIfInitiallyEmpty("frstJncmpYmd") ? "#fff" : "#F6F6F6"}
                    textAlign="left"
                    calendarThemeColor={AppContext.themeColor}
                    value={frstJncmpYmd}
                    disabled={!canEditIfInitiallyEmpty("frstJncmpYmd")}
                    onChange={(data: string) => {
                        if (!canEditIfInitiallyEmpty("frstJncmpYmd")) return;
                        setFrstJncmpYmd(data);
                    }}
                />
            ),
        });
    }
    
    if (isLeave) {
        optionalFields.push(
            {
                key    : "leaveTypeCd",
                label  : "휴직분류",
                content: (
                    <SelectBox
                        value={leaveTypeCd}
                        width={230}
                        height={36}
                        fontSize={14}
                        fontFamily="fm-c"
                        color="#333"
                        letterSpacing={-1}
                        borderColor="#ddd"
                        borderOnColor="#ddd"
                        borderRadius={5}
                        background="#F6F6F6"
                        codeArray={Util.getCodeArray("LEAVE_TYPE_CD")}
                        placeholder=""
                        paddingRight={8}
                        disabled={true}
                    />
                ),
            },
            {
                key    : "leaveStartDt",
                label  : "휴직 시작일",
                content: (
                    <TextBox
                        value={Util.cvtDateStrForm(leaveStartDt, "-", "YYYYMMDD")}
                        width={230}
                        height={36}
                        fontSize={14}
                        fontFamily="fm-c"
                        color="#333"
                        letterSpacing={-1}
                        borderColor="#ddd"
                        borderOnColor="#ddd"
                        borderRadius={5}
                        style={{
                            backgroundColor : "#F6F6F6",
                            padding         : "0 10px",
                        }}
                        mode="readonly"
                    />
                ),
            },
            {
                key    : "leaveEndDt",
                label  : "휴직 종료일",
                content: (
                    <TextBox
                        value={
                            Util.isEmpty(leaveEndDt)
                                ? ""
                                : `${Util.cvtDateStrForm(leaveEndDt, "-", "YYYYMMDD")} / 잔여 ${remainDay}일`
                        }
                        width={230}
                        height={36}
                        fontSize={14}
                        fontFamily="fm-c"
                        color="#333"
                        letterSpacing={-1}
                        borderColor="#ddd"
                        borderOnColor="#ddd"
                        borderRadius={5}
                        style={{ backgroundColor: "#F6F6F6", padding: "0 10px" }}
                        mode="readonly"
                    />
                ),
            },
        );
    }

    optionalFields.push({
        key    : "wthdAgreCd",
        label  : "원천징수",
        content: (
            <SelectBox
                value={wthdAgreCd}
                width={230}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#ddd"
                borderRadius={5}
                background="#F6F6F6"
                codeArray={Util.getCodeArray("WTHD_STATUS_CD")}
                placeholder=""
                paddingRight={8}
                disabled={true}
            />
        ),
    });

    if (showJoinDateFields) {
        optionalFields.push(
            {
                key    : "joinYmd",
                label  : "가입신청일",
                content: (
                    <DatePickerBox
                        width={230}
                        height={36}
                        fontSize={14}
                        fontFamily="fm-c"
                        color="#333"
                        letterSpacing={-1}
                        borderColor="#ddd"
                        borderOnColor={canEditIfInitiallyEmpty("joinYmd") ? "#222" : "#ddd"}
                        borderRadius={5}
                        bgColor={canEditIfInitiallyEmpty("joinYmd") ? "#fff" : "#F6F6F6"}
                        textAlign="left"
                        calendarThemeColor={AppContext.themeColor}
                        value={joinYmd}
                        disabled={!canEditIfInitiallyEmpty("joinYmd")}
                        onChange={(data: string) => {
                            if (!canEditIfInitiallyEmpty("joinYmd")) return;
                            setJoinYmd(data);
                        }}
                    />
                ),
            },
            {
                key    : "aprvYmd",
                label  : "가입승인일",
                content: (
                    <DatePickerBox
                        width={230}
                        height={36}
                        fontSize={14}
                        fontFamily="fm-c"
                        color="#333"
                        letterSpacing={-1}
                        borderColor="#ddd"
                        borderOnColor={canEditIfInitiallyEmpty("aprvYmd") ? "#222" : "#ddd"}
                        borderRadius={5}
                        bgColor={canEditIfInitiallyEmpty("aprvYmd") ? "#fff" : "#F6F6F6"}
                        textAlign="left"
                        calendarThemeColor={AppContext.themeColor}
                        value={aprvYmd}
                        disabled={!canEditIfInitiallyEmpty("aprvYmd")}
                        onChange={(data: string) => {
                            if (!canEditIfInitiallyEmpty("aprvYmd")) return;
                            setAprvYmd(data);
                        }}
                    />
                ),
            },
            {
                key    : "rglrRegMm",
                label  : "조합원 등록월",
                content: (
                    <TextBox
                        value={Util.cvtMonthStrForm(rglrRegMm)}
                        width={230}
                        height={36}
                        fontSize={14}
                        fontFamily="fm-c"
                        color="#333"
                        letterSpacing={-1}
                        borderColor="#ddd"
                        borderOnColor="#ddd"
                        borderRadius={5}
                        placeholder="가입승인일 기준 자동세팅"
                        style={{
                            backgroundColor : "#F6F6F6",
                            padding         : "0 10px",
                        }}
                        mode="readonly"
                    />
                ),
            },
        );
    }

    return (
        <>
            <div className="w100p dpFlx jcC bsBdb">
                <div className="w680">
                    {/* 1행 */}
                    <div className="dpFlx flxNowrap gap20 jcC pt10">
                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">성명</div>
                            <div className="flx1">
                                <TextBox
                                    value={mbrasNm}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor="#ddd"
                                    borderRadius={5}
                                    style={{
                                        backgroundColor : "#F6F6F6",
                                        padding         : "0 10px",
                                    }}
                                    mode="readonly"
                                    onChangeFunc={(text: string) => setMbrasNm(text)}
                                />
                            </div>
                        </div>

                        <div className="w330 dpFlx aiC">
                            <div className="w100 dpFlx aiC jcSb fm-b fs13 ls06 fc111111">
                                <span>휴대전화번호</span>
                                {!editableEmptyFields.telno && isMemberAdmin && !isReadOnlyStatus && (
                                    <div className="mr8" title="기존 입력값 수정">
                                        <CheckBox
                                            checked={telnoEdit}
                                            buttonColor={AppContext.themeColor}
                                            borderColor="#999999"
                                            borderOnColor={AppContext.themeColor}
                                            backgroundColor="#F6F6F6"
                                            onChange={() => setTelnoEdit(!telnoEdit)}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flx1">
                                <TextBox
                                    value={Util.setPhoneNum(telno)}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditTelno ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    style={{
                                        backgroundColor : canEditTelno ? "#fff" : "#F6F6F6",
                                        padding         : "0 10px",
                                    }}
                                    mode={canEditTelno ? "" : "readonly"}
                                    onChangeFunc={(text: string) => {
                                        if (!canEditTelno) return;
                                        setTelno(text.replace(/[^0-9]/g, ""));
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 2행 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">생년월일</div>
                            <div className="flx1">
                                <DatePickerBox
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditIfInitiallyEmpty("brdtDate") ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    bgColor={canEditIfInitiallyEmpty("brdtDate") ? "#fff" : "#F6F6F6"}
                                    textAlign="left"
                                    calendarThemeColor={AppContext.themeColor}
                                    value={brdtDate}
                                    disabled={!canEditIfInitiallyEmpty("brdtDate")}
                                    onChange={(data: string) => setBrdtDate(data)}
                                />
                            </div>
                        </div>

                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">성별</div>
                            <div className="flx1">
                                <SelectBox
                                    value={gndrCd}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditIfInitiallyEmpty("gndrCd") ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    background={canEditIfInitiallyEmpty("gndrCd") ? "#fff" : "#f6f6f6"}
                                    codeArray={Util.getCodeArray("GNDR_CD")}
                                    paddingRight={8}
                                    disabled={!canEditIfInitiallyEmpty("gndrCd")}
                                    onChangeFunc={(data: string) => {
                                        if (!canEditIfInitiallyEmpty("gndrCd")) return;
                                        setGndrCd(data);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 3행 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">소속본부</div>
                            <div className="flx1">
                                <SelectBox
                                    value={upOgnzCd}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditIfInitiallyEmpty("upOgnzCd") ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    background={canEditIfInitiallyEmpty("upOgnzCd") ? "#fff" : "#f6f6f6"}
                                    codeArray={Util.getUpOrgCodeArray("01")}
                                    paddingRight={8}
                                    disabled={!canEditIfInitiallyEmpty("upOgnzCd")}
                                    onChangeFunc={(data: string) => {
                                        if (!canEditIfInitiallyEmpty("upOgnzCd")) return;
                                        setUpOgnzCd(data);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">근로형태</div>
                            <div className="flx1">
                                <SelectBox
                                    value={lbrShpSeCd}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditIfInitiallyEmpty("lbrShpSeCd") ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    background={canEditIfInitiallyEmpty("lbrShpSeCd") ? "#fff" : "#f6f6f6"}
                                    codeArray={Util.getCodeArray("LBR_SHP_SE_CD")}
                                    paddingRight={8}
                                    disabled={!canEditIfInitiallyEmpty("lbrShpSeCd")}
                                    onChangeFunc={(data: string) => {
                                        if (!canEditIfInitiallyEmpty("lbrShpSeCd")) return;

                                        if (data !== lbrShpSeCd) {
                                            setOcptTypeCd("");
                                        }

                                        setLbrShpSeCd(data);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 4행 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">소속지부</div>
                            <div className="flx1">
                                <SelectBox
                                    value={ogdpOrgCd}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditIfInitiallyEmpty("ogdpOrgCd") ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    background={canEditIfInitiallyEmpty("ogdpOrgCd") ? "#fff" : "#f6f6f6"}
                                    codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                                    paddingRight={8}
                                    disabled={!canEditIfInitiallyEmpty("ogdpOrgCd")}
                                    onChangeFunc={(data: string) => {
                                        if (!canEditIfInitiallyEmpty("ogdpOrgCd")) return;
                                        setOgdpOrgCd(data);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="w330 dpFlx aiC">
                            <div className="w100 dpFlx aiC jcSb fm-b fs13 ls06 fc111111">
                                <span>직종</span>
                                {!editableEmptyFields.ocptTypeCd && isMemberAdmin && !isReadOnlyStatus && (
                                    <div className="mr8" title="기존 입력값 수정">
                                        <CheckBox
                                            checked={ocptTypeEdit}
                                            buttonColor={AppContext.themeColor}
                                            borderColor="#999999"
                                            borderOnColor={AppContext.themeColor}
                                            backgroundColor="#F6F6F6"
                                            onChange={() => setOcptTypeEdit(!ocptTypeEdit)}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flx1">
                                <SelectBox
                                    value={ocptTypeCd}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditOcptType ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    background={canEditOcptType ? "#fff" : "#f6f6f6"}
                                    codeArray={ocptGroupCd ? Util.getCodeArray(ocptGroupCd) : []}
                                    placeholder={Util.isEmpty(lbrShpSeCd) ? "근로형태를 먼저 선택" : "직종 선택"}
                                    paddingRight={8}
                                    disabled={!canEditOcptType || Util.isEmpty(lbrShpSeCd) || Util.isEmpty(ocptGroupCd)}
                                    onChangeFunc={(data: string) => {
                                        if (!canEditOcptType) return;
                                        setOcptTypeCd(data);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 5행 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w330 dpFlx aiC">
                            <div className="w100 dpFlx aiC jcSb fm-b fs13 ls06 fc111111">
                                <span>관내</span>
                                {!editableEmptyFields.wtjrNm && isMemberAdmin && !isReadOnlyStatus && (
                                    <div className="mr8" title="기존 입력값 수정">
                                        <CheckBox
                                            checked={wtjrNmEdit}
                                            buttonColor={AppContext.themeColor}
                                            borderColor="#999999"
                                            borderOnColor={AppContext.themeColor}
                                            backgroundColor="#F6F6F6"
                                            onChange={() => setWtjrNmEdit(!wtjrNmEdit)}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flx1">
                                <TextBox
                                    value={wtjrNm}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditWtjrNm ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    style={{ backgroundColor : canEditWtjrNm ? "#fff" : "#f6f6f6", padding : "0 10px",}}
                                    mode={canEditWtjrNm ? "" : "readonly"}
                                    onChangeFunc={(text: string) => {
                                        if (!canEditWtjrNm) return;
                                        setWtjrNm(text);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="w330 dpFlx aiC">
                            <div className="w100 fm-b fs13 ls06 fc111111">상태</div>
                            <div className="flx1">
                                <SelectBox
                                    value={mbrasJoinSttsCd}
                                    width={230}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor={canEditIfInitiallyEmpty("mbrasJoinSttsCd") ? "#222" : "#ddd"}
                                    borderRadius={5}
                                    background={canEditIfInitiallyEmpty("mbrasJoinSttsCd") ? "#fff" : "#F6F6F6"}
                                    paddingRight={8}
                                    disabled={!canEditIfInitiallyEmpty("mbrasJoinSttsCd")}
                                    codeArray={(() => {
                                        const arr = Util.getCodeArray("MBRAS_JOIN_STTS_CD");
                                        const withoutWithdraw = arr.filter((c: any) => String(c.code) !== "03");

                                        if (isWithdraw) {
                                            const withdraw = arr.find((c: any) => String(c.code) === "03");
                                            return withdraw ? [withdraw, ...withoutWithdraw] : withoutWithdraw;
                                        }

                                        return withoutWithdraw;
                                    })()}
                                    onChangeFunc={(data: string) => {
                                        if (!canEditIfInitiallyEmpty("mbrasJoinSttsCd")) return;
                                        setMbrasJoinSttsCd(data);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 상태별 추가정보 */}
                    <div className="dpFlx flxWrap mt10" style={{ columnGap: "20px", rowGap: "10px" }}>
                        {optionalFields.map((field) => (
                            <div
                                key={field.key}
                                className="w330 dpFlx aiC"
                            >
                                <div className="w100 fm-b fs13 ls06 fc111111">
                                    {field.label}
                                </div>
                                <div className="flx1">
                                    {field.content}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 주소 */}
                    <div className="dpFlx aiC mt10">
                        <div className="w100 fm-b fs13 ls06 fc111111">주소</div>
                        <div className="flx1">
                            <TextBox
                                value={addr}
                                width={580}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor={canEdit ? "#222" : "#ddd"}
                                borderRadius={5}
                                style={{
                                    backgroundColor : canEdit ? "#fff" : "#F6F6F6",
                                    padding         : "0 10px",
                                }}
                                mode={canEdit ? "" : "readonly"}
                                onChangeFunc={(text: string) => {
                                    if (!canEdit) return;
                                    setAddr(text);
                                }}
                            />
                        </div>
                    </div>

                    {/* 메모 */}
                    <div className="dpFlx mt10">
                        <div className="w100 fm-b fs13 ls06 fc111111 pt8">메모</div>
                        <div className="flx1">
                            <textarea
                                value={memo}
                                className="w580 h46 p10 bdrd5 bd1 bdcDDDDDD fm-c fs14 fc333333 bsBdb"
                                style={{
                                    resize          : "none",
                                    backgroundColor : canEdit ? "#fff" : "#F6F6F6",
                                }}
                                placeholder={canEdit ? "조합원 관련 메모를 입력하세요" : ""}
                                readOnly={!canEdit}
                                onChange={(text) => {
                                    if (!canEdit) return;
                                    setMemo(text.target.value);
                                }}
                            />
                        </div>
                    </div>

                    {/* 탈퇴사유 */}
                    {isWithdraw && (
                        <div className="dpFlx mt10">
                            <div className="w100 fm-b fs13 ls06 fc111111 pt8">탈퇴사유</div>
                            <div className="flx1">
                                <textarea
                                    value={whdwlRsnCn}
                                    className="w580 h46 p10 bdrd5 bd1 bdcDDDDDD fm-c fs14 fc333333 bsBdb"
                                    style={{ resize: "none", backgroundColor: "#f6f6f6" }}
                                    placeholder=""
                                    readOnly={true}
                                />
                            </div>
                        </div>
                    )}

                    {/* 가입/탈퇴 이력 */}
                    <div className="mb10 mt5 p14 bd1 bdcDDDDDD bdrd10 bgcFAFAFA bsBdb">
                            <div className="dpFlx aiC jcSb mb12">
                                <div className="fm-b fs14 fc111111">조합원 가입/탈퇴 이력</div>
                                <div className="dpFlx aiC mr10">
                                    <span className="fm-c fs13 fcBlack mr5">가입기간</span>
                                    <span className="fm-c fs13 fcBlack">{joinPeriod}</span>
                                </div>
                            </div>

                            <div className="bgcWhite w100p">
                                <table
                                    className="bd1 bdcDDDDDD bdrd2"
                                    style={{ borderCollapse: "collapse", width: "100%" }}
                                >
                                    <colgroup>
                                        <col style={{ width: "18%" }} />
                                        <col style={{ width: "27%" }} />
                                        <col style={{ width: "27%" }} />
                                        <col style={{ width: "28%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr className="bgcFFF5ED h30" style={{ borderBottom: "1px solid #ccc" }}>
                                            <th className="fm-b fs13 fc555555 taC">구분</th>
                                            <th className="fm-b fs13 fc555555 taC">신청일</th>
                                            <th className="fm-b fs13 fc555555 taC">승인일</th>
                                            <th className="fm-b fs13 fc555555 taC">등록월</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyList.length > 0 ? (
                                            historyList.map((history: any, index: number) => (
                                            <tr
                                                key={`${history.mbrasAtrzSeCd}-${history.atrsDt}-${index}`}
                                                className="h30"
                                                style={{ borderBottom: index < historyList.length - 1 ? "1px solid #ccc" : "none" }}
                                            >
                                                <td className="taC">
                                                    <span className="fm-c fs13 fc333333">
                                                        {Util.getValToCode("MBRAS_ATRZ_SE_CD", history.mbrasAtrzSeCd)}
                                                    </span>
                                                </td>
                                                <td className="taC">
                                                    <span className="fm-c fs13 fc333333">
                                                        {Util.cvtDateStrForm(history.applyDt, "-", "YYYYMMDD")}
                                                    </span>
                                                </td>
                                                <td className="taC">
                                                    <span className="fm-c fs13 fc333333">
                                                        {Util.cvtDateStrForm(history.atrsDt, "-", "YYYYMMDD")}
                                                    </span>
                                                </td>
                                                <td className="taC">
                                                    <span className="fm-c fs13 fc333333">
                                                        {Util.cvtMonthStrForm(history.regMm)}
                                                    </span>
                                                </td>
                                            </tr>
                                            ))
                                        ) : (
                                            <tr className="h30">
                                                <td className="taC" colSpan={4}>
                                                    <span className="fm-c fs13 fc333333">가입/탈퇴 이력이 없습니다.</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    {isMemberAdmin && (
                    /* 안내문구 */
                    <div className="mt10 taC">
                        <span className="fm-c fs12 fc4A90A4">
                            ※ 주소·메모와 미입력 항목은 바로 수정할 수 있으며, 휴대전화번호·직종·관내는 체크박스 선택 후 수정할 수 있습니다.
                        </span>
                    </div>
                    )}
                    
                    {/* 버튼 */}
                    <div className="dpFlx jcC mt25 mb20 gap10">
                        <Button
                            width={100}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styCloseBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="닫기"
                            onClickFunc={closeModal}
                        />
                        {isMemberAdmin && !isReadOnlyStatus && (
                            <Button
                                width={100}
                                height={40}
                                fontFamily="fm-b"
                                fontSize={16}
                                letterSpacing={-1}
                                variant="styTempBtn"
                                borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                buttonName="저장"
                                onClickFunc={saveMbras}
                            />
                        )}
                        <Button
                            width={130}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styTempBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="PDF 다운로드"
                            onClickFunc={handlePdfDownload}
                        />
                    </div>
                </div>
            </div>

            <div style={{ position: "fixed", left: "-9999px", top: 0, width: "210mm", backgroundColor: "#fff", zIndex: -1 }}>
                <div ref={printRef}>
                    <MbrasInfoPdf
                        item={item}
                        historyList={pdfHistoryList}
                        joinPeriod={joinPeriod}
                    />
                </div>
            </div>
        </>
    );
};

export default MbrasMainMng;